import { Builder } from '@builder.io/react'
import { navigate } from 'gatsby'
import React, { useEffect, useState } from 'react'

import { getCognitoId } from 'components/util/UserId'
import { triggerTagging } from 'components/util/common-utils'
import { HeadingClass, HeadingType } from 'enums'
import CustomHeading, { headingTypePropObj } from '../common/heading'

enum Alignment {
  Center = 'center',
  Left = 'left',
  Right = 'right'
}

interface Props {
  headingText: string
  headingTextTwo: string
  headingTextColor: string
  backgroundColor: string
  textAlignment: Alignment
  headingType: HeadingType
  headingClass: HeadingClass
  showArrow: boolean
  arrowImage: any
  arrowTop: string
  arrowLeft: string
  arrowWidth: string
  arrowImageMobile: any
  arrowTopMobile: string
  arrowLeftMobile: string
  arrowWidthMobile: string
  enableButton: boolean
  coupon: string
}

const SectionArrowButton = ({
  backgroundColor,
  headingTextColor,
  headingText,
  headingTextTwo,
  textAlignment,
  headingType,
  headingClass,
  showArrow,
  arrowImage,
  arrowTop,
  arrowLeft,
  arrowWidth,
  arrowImageMobile,
  arrowTopMobile,
  arrowLeftMobile,
  arrowWidthMobile,
  enableButton,
  coupon = ''
}: Props) => {
  const dogDetailsPage = 'dog-details'
  const [scrollToSection, setScrollToSection] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Find the first element with an ID containing "Route"
      const matchingElement = Array.from(document.querySelectorAll<HTMLElement>('[id]')).find(el =>
        el.id.includes('Route')
      )

      if (matchingElement) {
        setScrollToSection(matchingElement as HTMLElement)
      }
    }
  }, [])

  const getAlignmentClass = () => {
    if (textAlignment === Alignment.Right) {
      return 'text-right'
    }
    if (textAlignment === Alignment.Center) {
      return 'text-center'
    }
    return 'text-left'
  }

  const textAlignClass = getAlignmentClass()

  const [isLoggedIn, setIsLoggedIn] = useState(false)

  useEffect(() => {
    const authenticateUser = async () => {
      try {
        const { username } = await getCognitoId()

        if (username) {
          setIsLoggedIn(true)
        }
      } catch (e) {
        console.log(e)
      }
    }

    authenticateUser()
  }, [isLoggedIn])

  return (
    <div className={`mx-auto w-3/4 p-4 lg:w-full ${textAlignClass}`} style={{ backgroundColor }}>
      <div className="relative inline-flex w-fit lg:mb-4">
        <div className="flex flex-col lg:flex-row lg:space-x-2">
          <CustomHeading
            classList="text-center"
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {headingText}
          </CustomHeading>
          {headingTextTwo && (
            <CustomHeading
              classList="text-center"
              headingClass={headingClass}
              headingType={headingType}
              color={headingTextColor}
            >
              {headingTextTwo}
            </CustomHeading>
          )}
        </div>

        {showArrow && (
          <>
            {arrowImage && (
              <img
                className="absolute z-40 hidden lg:block"
                style={{ top: `${arrowTop}`, left: `${arrowLeft}`, width: `${arrowWidth}`, transform: `rotate(10deg)` }}
                src={arrowImage}
                alt="Arrow 1"
              />
            )}
            {arrowImageMobile && (
              <img
                className="absolute z-40 block lg:hidden"
                style={{
                  top: `${arrowTopMobile}`,
                  left: `${arrowLeftMobile}`,
                  width: `${arrowWidthMobile}`,
                  transform: `rotate(12deg)`
                }}
                src={arrowImageMobile}
                alt="Arrow 1"
              />
            )}
          </>
        )}
      </div>
      {enableButton && (
        <button
          type="button"
          className={` mx-auto mt-2 flex w-4/5 justify-center rounded-3xl border border-transparent bg-primary px-4 py-2 text-base font-medium hover:underline md:text-lg lg:w-1/4`}
          onClick={() => {
            // Check if there is a route section on the page
            const matchingRouteSection = Array.from(document.querySelectorAll<HTMLElement>('[id]')).find(el =>
              el.id.includes('Route')
            )
            if (isLoggedIn) {
              navigate('/account/')
            } else if (matchingRouteSection) {
              triggerTagging(`section_arrow_button_cta__${matchingRouteSection.id}`, `${matchingRouteSection.id}`)
              matchingRouteSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } else {
              window.location.href = `/signup/${dogDetailsPage}/${coupon ? `?coupon=${coupon}` : ''}`
            }
          }}
          style={{ color: '#fff' }}
        >
          {scrollToSection ? 'Get Started' : (isLoggedIn ? 'Update' : 'Create') + ' Your Plan'}
        </button>
      )}
    </div>
  )
}

export default SectionArrowButton

Builder.registerComponent(SectionArrowButton, {
  name: 'SectionArrowButton',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      ...headingTypePropObj
    },
    {
      name: 'headingClass',
      type: 'string',
      enum: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'altH1', 'altH2', 'altH3', 'altH4', 'altH5', 'altH6'],
      defaultValue: 'H1'
    },
    {
      name: 'headingText',
      type: 'string',
      defaultValue: 'Sample Heading'
    },
    {
      name: 'headingTextTwo',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'center'
    },
    {
      name: 'showArrow',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'arrowImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'arrowTop',
      type: 'string',
      defaultValue: 'full'
    },
    {
      name: 'arrowLeft',
      type: 'string',
      defaultValue: 'full'
    },
    {
      name: 'arrowWidth',
      type: 'string',
      defaultValue: '8rem'
    },
    {
      name: 'arrowImageMobile',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'arrowTopMobile',
      type: 'string',
      defaultValue: 'full'
    },
    {
      name: 'arrowLeftMobile',
      type: 'string',
      defaultValue: 'full'
    },
    {
      name: 'arrowWidthMobile',
      type: 'string',
      defaultValue: '8rem'
    },
    {
      name: 'enableButton',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'coupon',
      type: 'text',
      defaultValue: ''
    }
  ]
})
